import type { MerchantReturnPolicy } from 'schema-dts';
import { ProductTypeKey } from 'shared/types/product/Product';
import { ExtendedVariant } from 'shared/types/product/Variant';
import { SHOP_ORIGIN } from '../../../components/commercetools-ui/organisms/home-brand-refresh/breadcrumb';
import { UIProduct } from '../../../components/commercetools-ui/organisms/product/product-details/types';

// Per-offer return policy for Google merchant listings, mirroring /return-policy/ (which mirrors
// the Terms of Service). It has to be per offer, not one Organization-level policy: Google applies
// an organization policy to every product, and most of the catalog is made to order and final sale.
// Iron balusters and unopened cable spools are also returnable under the terms but aren't sold in
// the shop, so they have no rule here.

const RETURN_POLICY_URL = `${SHOP_ORIGIN}/return-policy/`;
// The shop only ships within the US.
const RETURN_COUNTRY = 'US';

// "Stock hardware and tension fittings" in the terms.
const STOCK_HARDWARE_TYPES: ProductTypeKey[] = ['hardware', 'cable-railing-tension-kit', 'screw', 'washer'];

// Only the black, surface-mounted Express posts are stocked; side-mount and other colors are built to order.
const isBlackSurfaceMountExpressPost = (product: UIProduct, variant: ExtendedVariant) =>
  product.type === 'cable-railing-post' &&
  !!product.categories?.some((category) => category.slug === 'express-surface-mount-posts') &&
  String(variant.attributes?.color ?? '').startsWith('Standard Black');

export const isReturnable = (product: UIProduct, variant: ExtendedVariant) =>
  isBlackSurfaceMountExpressPost(product, variant) ||
  // Express handrail comes in black only, so it carries no color attribute to check.
  product.type === 'cable-railing-express-handrail' ||
  (!!product.type && STOCK_HARDWARE_TYPES.includes(product.type));

// Only final-sale items get markup. Google counts merchantReturnDays from delivery, while the terms
// count the 180-day window from shipment, so a finite-window policy would overstate it. Returnable
// items publish no return policy until the window is restated in delivery terms (or set in
// Merchant Center), rather than one that doesn't match the terms.
// An unknown type (the backend's product-type lookup was unavailable) publishes nothing too, rather
// than labelling a possibly returnable item final sale.
export const mapReturnPolicy = (product: UIProduct, variant: ExtendedVariant): MerchantReturnPolicy | undefined =>
  !product.type || isReturnable(product, variant)
    ? undefined
    : {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: RETURN_COUNTRY,
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
        merchantReturnLink: RETURN_POLICY_URL,
      };
